import Stack from "@mui/material/Stack";
import { EmbeddedMod } from "@src/store/slices/experienceBuilder/types";
import { IgToggleButtonGroup } from "@intelligems/ig-theme/IgToggleButtonGroup";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import React from "react";
import { LucideImage, LucideLayoutPanelLeft, LucideType } from "lucide-react";
import { useDispatch } from "@src/store";
import { ExperienceBuilderAction } from "@src/store/slices/experienceBuilder";
import { capitalize, ToggleButton } from "@mui/material";
import { EmbeddedNode } from "@intelligems/ig-types/schemas/plugin/plugin/embedded";

interface ElementSelectorProps {
  mod: EmbeddedMod;
}

export const ElementSelector = ({ mod }: ElementSelectorProps) => {
  const dispatch = useDispatch();

  if (!("nodes" in mod) || mod.nodes.length < 2) {
    return null;
  }
  const buttons = [...mod.nodes]
    .sort((a, b) => getOrder(a) - getOrder(b))
    .map((n) => <ElementButton key={n.selector} node={n} />);

  return (
    <Stack direction={"row"} alignItems={"center"} gap={1}>
      <IgTooltip
        title="We have identified multiple relevant elements in your selection."
        placement="top"
        sx={{ fontSize: 12 }}
      >
        <IgTypography variant="body2" color="text.secondary">
          Change:
        </IgTypography>
      </IgTooltip>
      <IgToggleButtonGroup
        value={mod.selector}
        sx={{ height: "24px" }}
        control={{
          exclusive: true,
          onChange: (_, selector) => {
            void dispatch(ExperienceBuilderAction.basicMod.swapDraft(selector));
          },
        }}
        buttons={buttons}
      />
    </Stack>
  );
};

function getOrder(node: EmbeddedNode) {
  switch (node.type) {
    case "text":
      return 0;
    case "image":
      return 1;
    case "layout":
      return node.subtype === "container" ? 3 : 2;
  }
}

interface ElementButtonProps {
  node: EmbeddedNode;
}

const ElementButton = ({ node }: ElementButtonProps) => {
  const data = {
    label: "",
    icon: null as React.ReactNode,
  };
  switch (node.type) {
    case "text":
      data.icon = <LucideType size={16} />;
      data.label = "Text";
      break;
    case "image":
      data.icon = <LucideImage size={16} />;
      data.label = "Image";
      break;
    case "layout":
      data.icon = <LucideLayoutPanelLeft size={16} />;
      data.label = capitalize(node.subtype);
      break;
  }

  return (
    <ToggleButton
      sx={{ padding: "8px", border: 0, borderRadius: 0 }}
      value={node.selector}
    >
      <IgTooltip title={data.label} placement="top">
        {data.icon}
      </IgTooltip>
    </ToggleButton>
  );
};
